import {
    ArrowDropDownRounded,
    BookmarkRounded,
    DeleteOutlineRounded,
    DeleteRounded,
} from '@mui/icons-material';
import Tag from './Common/Tag';
import { useEffect, useState } from 'react';
import {
    Box,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
} from '@mui/material';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { contactFiltersQueryOptions } from '@utils/queryOptions';
import { isEmpty, isEqual, isEqualWith } from 'lodash';
import { useNavigate } from '@tanstack/react-router';
import useDialog from '@hooks/useDialog';
import { useTranslation } from 'react-i18next';
import DialogTextField from './General/dialog-content/text-field';
import { useForm } from 'react-hook-form';

const SavedFilters = ({
    currentFilters,
    applySavedFilter,
    selectedFilter,
    savedFilters,
    mutationKey,
    refetchFilters,
}) => {
    const { t } = useTranslation();
    const [anchorEL, setAnchorEl] = useState(null);
    const dialog = useDialog();
    const { control } = useForm();

    const createFilterMutation = useMutation({
        mutationKey: ['create', mutationKey],
        onSuccess: () => {
            dialog.close();
            refetchFilters();
        },
    });

    const deleteFilterMutation = useMutation({
        mutationKey: ['delete', mutationKey],
        onSuccess: () => {
            dialog.close();
            refetchFilters();
        },
    });

    const handleSaveFilter = () => {
        dialog.open({
            title: t('Save Filter'),
            content: (
                <DialogTextField
                    label={t('Filter Name')}
                    control={control}
                    name="name"
                />
            ),
            confirmLabel: t('Save'),
            onConfirm: () => ({
                ...control._formValues,
                filter: currentFilters,
            }),
            confirmMutation: createFilterMutation,
        });
    };

    const handleDeleteFilter = filter => {
        dialog.open({
            title: t('Delete Filter') + '?',
            content: t('prompts:content.deleteFilter'),
            confirmLabel: t('Delete'),
            onConfirm: () => filter.id,
            confirmMutation: deleteFilterMutation,
            confirmButtonProps: {
                color: 'error',
            },
        });
    };

    return (
        <>
            <Tag
                label={selectedFilter?.name || t('Saved Filters')}
                variant="tonal"
                deleteIcon={<ArrowDropDownRounded />}
                onDelete={e => setAnchorEl(e.currentTarget.parentElement)}
                onClick={e => setAnchorEl(e.currentTarget)}
            />
            <Menu
                anchorEl={anchorEL}
                open={Boolean(anchorEL)}
                onClose={() => setAnchorEl(null)}
                sx={{
                    maxHeight: theme => theme.spacing(39),
                }}
                disablePortal
            >
                {savedFilters.map(filter => (
                    <MenuItem
                        key={filter.id}
                        selected={filter.id === selectedFilter?.id}
                        onClick={() => {
                            setAnchorEl(null);
                            applySavedFilter(filter.filter);
                        }}
                    >
                        <ListItemText>{filter.name}</ListItemText>
                        <ListItemIcon
                            sx={{
                                justifyContent: 'end',
                            }}
                        >
                            <IconButton
                                onClick={e => {
                                    e.stopPropagation();
                                    handleDeleteFilter(filter);
                                }}
                                size="small"
                            >
                                <DeleteRounded fontSize="small" />
                            </IconButton>
                        </ListItemIcon>
                    </MenuItem>
                ))}

                <Box
                    sx={{
                        position: 'sticky',
                        bottom: 0,
                        backgroundColor: '#f3f3f6',
                        mb: -1,
                        pb: 1,
                    }}
                >
                    <Divider
                        sx={{
                            my: 1,
                        }}
                    />
                    <MenuItem
                        disabled={isEmpty(currentFilters)}
                        onClick={handleSaveFilter}
                    >
                        <ListItemIcon>
                            <IconButton disableRipple size="small" edge="start">
                                <BookmarkRounded fontSize="small" />
                            </IconButton>
                        </ListItemIcon>
                        <ListItemText>{t('Save Current Filter')}</ListItemText>
                    </MenuItem>
                </Box>
            </Menu>
        </>
    );
};

export default SavedFilters;
